import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  alpha,
  useTheme
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { Chatbot, KeywordResponse, User } from '../../types/chatbot';

interface ChatbotsListProps {
  chatbots: Chatbot[];
  userProfile: User | null;
  onEdit: (chatbot: Chatbot) => void;
  onDelete: (chatbot: Chatbot) => void;
  onAdd: () => void;
  onInstall: (chatbot: Chatbot) => void;
}

const ChatbotsList: React.FC<ChatbotsListProps> = ({
  chatbots,
  userProfile,
  onEdit,
  onDelete,
  onAdd,
  onInstall
}) => {
  const theme = useTheme();

  return (
    <>
      <Grid container spacing={3}>
        {chatbots.map((chatbot) => (
          <Grid item xs={12} sm={6} md={4} key={chatbot._id}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" component="h2">
                    {chatbot.name}
                  </Typography>
                  <Tooltip title="Installation Instructions">
                    <IconButton
                      size="small"
                      onClick={() => onInstall(chatbot)}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                  {chatbot.welcomeMessage}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={chatbot.position}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                  <Chip
                    label={chatbot.settings.businessHours.enabled ? 'Business Hours Enabled' : 'Business Hours Disabled'}
                    size="small"
                    color={chatbot.settings.businessHours.enabled ? 'success' : 'default'}
                    sx={{ borderRadius: 1.5 }}
                  />
                  {chatbot.trialStatus.isInTrial && chatbot.trialStatus.firstApiCallDate && (
                    <Tooltip 
                      title={`Trial started: ${new Date(chatbot.trialStatus.trialStartDate).toLocaleDateString()}
Trial ends: ${new Date(chatbot.trialStatus.trialEndDate).toLocaleDateString()}`}
                      arrow
                    >
                      <Chip
                        label="Trial Active"
                        size="small"
                        color="warning"
                        sx={{ borderRadius: 1.5, cursor: 'help' }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {chatbot.keywordResponses && chatbot.keywordResponses.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#666' }}>
                      Keyword Responses:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                      {chatbot.keywordResponses.map((response: KeywordResponse) => (
                        <Chip
                          key={response._id}
                          label={`${response.keywords.join(', ')} (${response.priority})`}
                          size="small"
                          color={response.isActive ? 'success' : 'default'}
                          sx={{ borderRadius: 1.5, mb: 1 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
              <CardActions sx={{ px: 3, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onEdit(chatbot)}
                  sx={{ 
                    textTransform: 'none',
                    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#1a237e',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : 'rgba(26, 35, 126, 0.04)'
                    }
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => onDelete(chatbot)}
                  sx={{ 
                    textTransform: 'none',
                    color: theme.palette.mode === 'dark' ? theme.palette.error.light : undefined,
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.1) : 'rgba(211, 47, 47, 0.04)'
                    }
                  }}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add Chatbot Button */}
      {userProfile?.subscription.status === 'active' &&
       chatbots.length < userProfile?.subscription.maxChatbots && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ 
            mt: 4,
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '#0d1642'
            },
            textTransform: 'none',
            px: 4,
            py: 1.5,
            borderRadius: 1.5
          }}
        >
          Add Chatbot
        </Button>
      )}
    </>
  );
};

export default ChatbotsList; 